import { Link } from 'react-router-dom';
import { BookOpen, FileText, ClipboardList, MessageSquare, User, ChevronRight, Sparkles } from 'lucide-react';

type Props = {
  name: string;
  onboardingComplete: boolean;
  onboardingSteps: number;
  onboardingTotal: number;
  profileComplete: number; // 0-100
  hasTraining: boolean;
};

export default function WelcomePanel({ name, onboardingComplete, onboardingSteps, onboardingTotal, profileComplete, hasTraining }: Props) {
  // Only show for new/incomplete users
  if (onboardingComplete && profileComplete >= 80) return null;

  const items = [
    !onboardingComplete && {
      icon: ClipboardList,
      label: 'Complete Onboarding',
      desc: `${onboardingSteps}/${onboardingTotal} steps done`,
      href: '/onboarding',
      urgent: true,
    },
    profileComplete < 80 && {
      icon: User,
      label: 'Complete Your Profile',
      desc: `${profileComplete}% complete`,
      href: '/profile',
      urgent: false,
    },
    hasTraining && {
      icon: BookOpen,
      label: 'Start Your Training',
      desc: 'View assigned modules',
      href: '/modules',
      urgent: false,
    },
    {
      icon: FileText,
      label: 'Read Policies',
      desc: 'Company policies & handbook',
      href: '/policies',
      urgent: false,
    },
    {
      icon: MessageSquare,
      label: 'Check Messages',
      desc: 'See announcements & notifications',
      href: '/notifications',
      urgent: false,
    },
  ].filter(Boolean) as { icon: React.FC<{ className?: string }>; label: string; desc: string; href: string; urgent: boolean }[];

  return (
    <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-4 h-4 text-primary" />
        <h2 className="text-sm font-semibold text-foreground">Welcome to The Rattlebone{name ? `, ${name}` : ''}!</h2>
      </div>
      <p className="text-xs text-muted-foreground mb-3">Here's what to do first to get set up:</p>
      <div className="space-y-1.5">
        {items.slice(0, 4).map(item => {
          const Icon = item.icon;
          return (
            <Link
              key={item.href}
              to={item.href}
              className={`flex items-center gap-3 rounded-lg px-3 py-2 no-underline transition-colors ${
                item.urgent ? 'bg-primary/10 border border-primary/20' : 'bg-card border border-border hover:bg-muted'
              }`}
            >
              <Icon className={`w-4 h-4 shrink-0 ${item.urgent ? 'text-primary' : 'text-muted-foreground'}`} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground">{item.label}</p>
                <p className="text-xs text-muted-foreground">{item.desc}</p>
              </div>
              <ChevronRight className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
            </Link>
          );
        })}
      </div>
    </div>
  );
}
